import { useEffect } from 'react';
import classNames from 'classnames';

import { useToasts } from 'dibs-toaster/exports/useToasts';

import { SbSharedFavoritesNudgeToastContent } from './SbSharedFavoritesNudgeToastContent';
import { useFavoritesNudge } from '../useFavoritesNudge';
import { getFavoritesNudgeDisplayedCount } from '../helpers/getFavoritesNudgeDisplayedCount';
import {
    trackFavoritingNudgeDisplayed,
    trackFavoritingNudgeDismissed,
    trackSbSharedFavoritesNudgeContainerClicked,
    type ItemFavoritingNudgeTrigger,
} from '../tracking';
import {
    isItemFavoritingFeb2026Variant,
    trackItemFavoritingFeb2026Test,
} from 'dibs-buyer-item-favoriting/src/abTests';

import dibsCss from 'dibs-css';
import styles from './SbSharedFavoritesNudgeToast.scss';

const useSbSharedFavoritesNudgeToast = ({
    userId,
    showNudge,
}: {
    userId: string;
    showNudge: boolean;
}): { isVisible: boolean } => {
    const isItemFavoritingVariantDesktop = isItemFavoritingFeb2026Variant();
    const { addToast } = useToasts();
    const { isVisible, hideNudge } = useFavoritesNudge({
        userId,
        itemId: null,
        showNudge,
    });

    const trigger: ItemFavoritingNudgeTrigger =
        getFavoritesNudgeDisplayedCount() > 1 ? 'sb longview' : 'first sb view';

    useEffect(() => {
        if (isVisible) {
            addToast({
                toast: {
                    type: 'basic',
                    position: 'bottomRight',
                    showCloseButton: true,
                    hideTimeoutBar: true,
                    closeAfterTimeout: false,
                    zIndex: 'belowNav',
                    withCustomBackground: true,
                    className: classNames(
                        dibsCss.overflowHidden,
                        !isItemFavoritingVariantDesktop && [
                            dibsCss.bgTangerine50,
                            styles.rotatingGradientBorder,
                        ],
                        isItemFavoritingVariantDesktop && styles.toastContainer
                    ),
                    closeClassName: classNames(
                        styles.close,
                        dibsCss.zLow,
                        !isItemFavoritingVariantDesktop && dibsCss.fillSatan,
                        isItemFavoritingVariantDesktop && dibsCss.fillNoir
                    ),
                    enterAnimationClassName: isItemFavoritingVariantDesktop
                        ? styles.enterFromBottom
                        : undefined,
                    children: <SbSharedFavoritesNudgeToastContent />,
                    onClose: () => {
                        trackFavoritingNudgeDismissed({
                            trigger,
                        });
                        hideNudge();
                    },
                    onContainerClick: () => {
                        if (isItemFavoritingVariantDesktop) {
                            trackSbSharedFavoritesNudgeContainerClicked({
                                trigger,
                            });
                        }
                    },
                },
            });
            if (isItemFavoritingVariantDesktop) {
                trackItemFavoritingFeb2026Test();
            }
            trackFavoritingNudgeDisplayed({
                trigger,
            });
        }
    }, [isVisible, addToast, hideNudge, isItemFavoritingVariantDesktop, trigger]);

    return { isVisible };
};

export { useSbSharedFavoritesNudgeToast };
